import { DOCUMENT_UPLOADED_BY } from 'constants/documents';

/**
 * One upload batch, as returned by both document endpoints under
 * `documentGroups[]`. `createdBy` / `comment` / `creationTime` describe the
 * batch; `uploadedBy` is its documents' value, since a batch is homogeneous.
 */
export interface DocumentGroup {
  id: string | null;
  createdBy: string | null;
  comment: string | null;
  creationTime: string | null;
  uploadedBy: string | null;
  documents: any[];
}

export interface ClassifiedDocumentGroups {
  fromCustomer: DocumentGroup[];
  forCustomer: DocumentGroup[];
}

/** Read the uniform `documentGroups[]` off a document endpoint response. */
export const toDocumentGroups = (response: any): DocumentGroup[] =>
  Array.isArray(response?.documentGroups) ? response.documentGroups : [];

/**
 * True only for an explicit BUYER upload. Anything else — VENDOR, missing,
 * empty or unrecognised — is treated as a vendor document, so a group is never
 * hidden just because its `uploadedBy` is absent or unexpected.
 */
export const isFromCustomer = (group?: DocumentGroup | null): boolean =>
  typeof group?.uploadedBy === 'string' &&
  group.uploadedBy.trim().toUpperCase() === DOCUMENT_UPLOADED_BY.BUYER;

/** True when any response carries at least one document. */
export const hasDocuments = (response: any): boolean =>
  toDocumentGroups(response).some(group => group.documents?.length > 0);

/**
 * Merge every response's groups and split them by who uploaded them. Groups are
 * never split: a batch belongs wholly to one side, so its date, comment and
 * email button stay intact. Empty groups are dropped so a heading is not
 * rendered for nothing.
 */
export const classifyDocumentGroups = (...responses: any[]): ClassifiedDocumentGroups => {
  const groups = responses
    .flatMap(toDocumentGroups)
    .filter(group => group?.documents?.length > 0);

  return {
    fromCustomer: groups.filter(isFromCustomer),
    forCustomer: groups.filter(group => !isFromCustomer(group)),
  };
};
